import { useState } from "react";

import { Flex, useDisclosure } from "@chakra-ui/react";
import { useIntl } from "react-intl";

import { Asset, AssetState, DamageType } from "__graphql__/types";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { TitleS } from "ui/Typography/Typography";

import { useDamageReportMutation, useUpdateAssetStateMutation } from "../queries/assets.generated";
import { useAssetManagementStore } from "../stores/assetManagementStore";
import { DAMAGE_TYPE_OPTIONS, ASSET_TYPE } from "../utils/constants";
import { BatteryScannedCard } from "./cards/BatteryScannedCard";
import { BatteryStatusReasonModal } from "./modals/BatteryStatusReasonModal";

export function BatteriesCountedList({
  functionalList,
  nonFunctionalList,
  highlightedBatteryId,
}: {
  functionalList: Asset[];
  nonFunctionalList: Asset[];
  highlightedBatteryId: string | null;
}) {
  const intl = useIntl();
  const { showToastUI } = useCustomToast();
  const { sendSegmentTrackEvent } = useAnalytics();

  const [updateAssetState, { loading: isUpdating }] = useUpdateAssetStateMutation();
  const { countedAssets, setCountedAssets } = useAssetManagementStore();
  const [assetIdToToggle, setAssetIdToToggle] = useState<string | null>(null);
  const [newStateToToggle, setNewStateToToggle] = useState<AssetState | null>(null);
  const [selectedNonFunctionalReason, setSelectedNonFunctionalReason] = useState<DamageType | null>(
    null,
  );

  const [createDamageReport] = useDamageReportMutation();

  const { currentTaskId, countSessionId } = useAssetManagementStore();

  const {
    isOpen: isBatteryStatusReasonModalOpen,
    onOpen: openBatteryStatusReasonModal,
    onClose: closeBatteryStatusReasonModal,
  } = useDisclosure();

  const handleUpdateState = async (assetId: string, newState: AssetState) => {
    if (!assetId) return;

    const isFunctional = newState === AssetState.asset_state_operational;

    try {
      await updateAssetState({
        variables: {
          input: {
            assetId,
            newState,
          },
        },
      });
      showToastUI({
        status: "success",
        description: isFunctional
          ? "pages.inventory.battery-management-page.toast.marked-functional"
          : "pages.inventory.battery-management-page.toast.marked-not-functional",
      });
      const updatedAssets = countedAssets.map((assetItem) =>
        assetItem.id === assetId ? { ...assetItem, state: newState } : assetItem,
      );
      setCountedAssets(updatedAssets);
    } catch {
      showToastUI({
        status: "error",
        description: "pages.inventory.battery-management-page.error.update-failed",
      });
    }
  };

  const handleCloseBatteryStatusReasonModal = () => {
    setAssetIdToToggle(null);
    setNewStateToToggle(null);
    setSelectedNonFunctionalReason(null);
    closeBatteryStatusReasonModal();
  };

  const handleBatteryStatusReason = async () => {
    if (!assetIdToToggle || !newStateToToggle || !selectedNonFunctionalReason) return;

    try {
      await createDamageReport({
        variables: {
          input: {
            assetId: assetIdToToggle,
            type: selectedNonFunctionalReason,
            description:
              DAMAGE_TYPE_OPTIONS.find((option) => option.key === selectedNonFunctionalReason)
                ?.label || null,
          },
        },
      });
      sendSegmentTrackEvent("assetDamagedReported", {
        asset_type: ASSET_TYPE,
        asset_count_process_id: currentTaskId!,
        asset_count_session_id: countSessionId!,
        flow_type: "battery_count",
        asset_id: assetIdToToggle,
        asset_state: newStateToToggle,
        reason: selectedNonFunctionalReason,
      });
      await handleUpdateState(assetIdToToggle, newStateToToggle);
      handleCloseBatteryStatusReasonModal();
      showToastUI({
        status: "success",
        description: "pages.inventory.battery-management-page.toast.reported-successfully",
      });
    } catch {
      showToastUI({
        status: "error",
        description: "pages.inventory.battery-management-page.toast.error.report-failed",
      });
      handleCloseBatteryStatusReasonModal();
    }
  };

  const handleToggleToFunctional = (assetId: string) => {
    handleUpdateState(assetId, AssetState.asset_state_operational);
  };
  const handleToggleToNonFunctional = (assetId: string) => {
    openBatteryStatusReasonModal();
    setAssetIdToToggle(assetId);
    setNewStateToToggle(AssetState.asset_state_defect);
  };
  return (
    <Flex w="full" flex="1" p="s200" gap="s300" direction="column">
      {functionalList.length > 0 && (
        <Flex gap="s200" direction="column">
          <TitleS>
            {intl.formatMessage(
              { id: "pages.inventory.battery-scan-page.functional" },
              { count: functionalList.length },
            )}
          </TitleS>
          {functionalList.map((battery) => (
            <BatteryScannedCard
              key={battery.id}
              state={battery.state}
              model={battery.model ?? undefined}
              serialNumber={battery.serialNumber}
              onToggle={() => handleToggleToNonFunctional(battery.id)}
              isToggling={isUpdating}
              isHighlighted={highlightedBatteryId === battery.serialNumber}
            />
          ))}
        </Flex>
      )}

      {nonFunctionalList.length > 0 && (
        <Flex gap="s200" direction="column">
          <TitleS>
            {intl.formatMessage(
              { id: "pages.inventory.battery-scan-page.non-functional" },
              { count: nonFunctionalList.length },
            )}
          </TitleS>
          {nonFunctionalList.map((battery) => (
            <BatteryScannedCard
              key={battery.id}
              state={battery.state}
              model={battery.model ?? undefined}
              onToggle={() => handleToggleToFunctional(battery.id)}
              isToggling={isUpdating}
              serialNumber={battery.serialNumber}
              isHighlighted={highlightedBatteryId === battery.serialNumber}
            />
          ))}
        </Flex>
      )}

      <BatteryStatusReasonModal
        isOpen={isBatteryStatusReasonModalOpen}
        onClose={handleCloseBatteryStatusReasonModal}
        onClickPrimary={handleBatteryStatusReason}
        onClickSecondary={handleCloseBatteryStatusReasonModal}
        selectedReason={selectedNonFunctionalReason}
        setSelectedReason={setSelectedNonFunctionalReason}
      />
    </Flex>
  );
}
